import {
  snackSupplierOptions,
  snackMenuOptions,
  snackMenuItemsOptions,
  useBuilding,
  updateCategoryVisibility,
  updateSnackMenu,
} from "@/api";
import CategoryModal from "@/components/snack/CategoryModal";
import ItemsCutoffModal from "@/components/snack/ItemsCutoffModal";
import CategoryTableActions from "@/components/snack/CategoryTableActions";
import { CutoffCell, MixedCutoffCell } from "@/components/snack/CutoffCell";
import Table from "@/components/table/Table";
import {
  firebaseTimestampToDate,
  FULL_DATE_LOCALE,
  getBuildingId,
  notifyApiError,
} from "@/lib";
import { useRouterStore, useSnackbarStore } from "@/store/zustandStore";
import { CutoffSettings, NotificationType, SnackCategory } from "@/types";
import { Breadcrumbs, Button, Icon } from "@app-components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import z from "zod";
import { arrayMove } from "@dnd-kit/sortable";
import SaveResetButtons from "@/components/SaveResetButtons";
import { usePrompt } from "@/hooks/usePrompt";
import PageBackButton from "@/components/PageBackButton";
import { useBuildingQuery } from "@/components/RouterSetup";
import { useConfirm } from "@/hooks/useConfirm";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import {
  DEFAULT_CUTOFF_SETTINGS,
  isSameCutoffSettings,
} from "@/lib/snackCutoff";

const columnHelper = createColumnHelper<SnackCategory>();

export const Route = createFileRoute("/_portal/snack-admin/menu/categories")({
  component: RouteComponent,
  loader: () => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.prefetchQuery(snackSupplierOptions(supplierCompanyId));
    queryClient.prefetchQuery(snackMenuOptions(supplierCompanyId));
    queryClient.prefetchQuery(snackMenuItemsOptions(supplierCompanyId, true));
  },
  validateSearch: z.object({
    create: z.coerce.boolean().optional(),
  }),
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  const navigate = Route.useNavigate();
  const { create } = Route.useSearch();
  const { addNotification } = useSnackbarStore();
  const { data: building } = useBuilding();
  const { data: menu, isLoading } = useBuildingQuery(
    snackMenuOptions(building.supplierCompanyId),
  );
  const { data: menuWithItems } = useBuildingQuery(
    snackMenuItemsOptions(building.supplierCompanyId, true),
  );
  const { data: supplier } = useBuildingQuery(
    snackSupplierOptions(building.supplierCompanyId),
  );
  const restaurantCutoffSettings =
    supplier?.orderingMode === "cutoff-v1"
      ? (supplier.cutoffSettings ?? DEFAULT_CUTOFF_SETTINGS)
      : undefined;
  const closedOnWeekends = supplier?.closedOnWeekends;
  const [localCategories, setLocalCategories] = useState<SnackCategory[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<SnackCategory | null>(
    null,
  );
  const [cutoffCategory, setCutoffCategory] = useState<SnackCategory | null>(
    null,
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const queryClient = useQueryClient();
  const { show: showConfirm } = useConfirm();

  usePrompt({
    isOpen: hasUnsavedChanges,
  });

  useEffect(() => {
    if (menu?.categories) {
      setLocalCategories(
        [...menu.categories].sort((a, b) => a.sortOrder - b.sortOrder),
      );
    }
  }, [menu?.categories]);

  const { mutate: updateMenuMutation, isPending: isUpdatingMenu } = useMutation(
    {
      mutationFn: (categories: SnackCategory[]) => {
        if (!menu) throw new Error("Menu not found");
        return updateSnackMenu(menu.id, {
          name: menu.name,
          email: menu.email,
          categories: categories.map((c, index) => ({
            ...c,
            sortOrder: index,
          })),
        });
      },
      onSuccess: () => {
        addNotification(
          t("NOTIFICATIONS.DOCUMENT_UPDATED", {
            entity: t("SIDEBAR.SNACK_CATEGORIES").toLowerCase(),
          }),
          NotificationType.SUCCESS,
        );
        setHasUnsavedChanges(false);
        queryClient.invalidateQueries({
          queryKey: [
            getBuildingId(),
            ...snackMenuOptions(building.supplierCompanyId).queryKey,
          ],
        });
      },
      onError: (error) => {
        notifyApiError(error, addNotification, t, {
          properties: { entity: t("SIDEBAR.SNACK_CATEGORIES").toLowerCase() },
          fallbackKey: "UPDATE_FAILED",
        });
      },
    },
  );

  const {
    mutate: updateCategoryVisibilityMutation,
    isPending: isUpdatingCategoryVisibility,
    variables: categoryVisibilityVariables,
  } = useMutation({
    mutationFn: ({
      menuId,
      categoryId,
      isHidden,
    }: {
      menuId: string;
      categoryId: string;
      categoryName: string;
      isHidden: boolean;
    }) => {
      return updateCategoryVisibility(menuId, categoryId, isHidden);
    },
    onSuccess: (_, { categoryName, isHidden }) => {
      addNotification(
        t("SNACK_ADMIN.MENU.CATEGORY.VISIBILITY_SUCCESS", {
          categoryName,
          isHidden: (isHidden ? t("HIDDEN") : t("VISIBLE")).toLowerCase(),
        }),
        NotificationType.SUCCESS,
      );
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "menu"],
      });
      queryClient.invalidateQueries({
        queryKey: [getBuildingId(), "menu-items"],
      });
    },
    onError: (_, { categoryName }) => {
      addNotification(
        t("NOTIFICATIONS.UPDATE_FAILED", { entity: `"${categoryName}"` }),
      );
    },
  });

  const cutoffsByCategory = useMemo(() => {
    const byCategory = new Map<
      string,
      { settings: CutoffSettings; isOverride: boolean }[]
    >();
    if (!restaurantCutoffSettings) return byCategory;
    for (const item of menuWithItems?.items ?? []) {
      const cutoffs = byCategory.get(item.categoryId) ?? [];
      cutoffs.push({
        settings: item.cutoffSettings ?? restaurantCutoffSettings,
        isOverride: !!item.cutoffSettings,
      });
      byCategory.set(item.categoryId, cutoffs);
    }
    return byCategory;
  }, [menuWithItems?.items, restaurantCutoffSettings]);

  const cutoffTarget = useMemo(() => {
    if (!cutoffCategory) return null;
    return {
      key: cutoffCategory.id,
      label: cutoffCategory.name[i18n.language as "en" | "no"],
      itemIds: (menuWithItems?.items ?? [])
        .filter((item) => item.categoryId === cutoffCategory.id)
        .map((item) => item.id),
      source: "category" as const,
    };
  }, [cutoffCategory, menuWithItems?.items, i18n.language]);

  const handleRowOrderChange = (oldIndex: number, newIndex: number) => {
    const newCategories = arrayMove(localCategories, oldIndex, newIndex);
    setLocalCategories(newCategories);
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    if (create) {
      setTimeout(() => {
        setModalOpen(true);
        navigate({
          search: (prev) => ({ ...prev, create: undefined }),
          replace: true,
        });
      }, 1000);
    }
  }, [create]);

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.sortOrder + 1, {
        id: "sortOrder",
        cell: (info) => {
          const originalOrder = info.getValue();
          const currOrder =
            localCategories.findIndex(
              (category) => category.id === info.row.original.id,
            ) + 1;
          const hasMoved = originalOrder !== currOrder;
          const movedUp = currOrder < originalOrder;
          const arrowRotateDeg = movedUp ? -90 : 90;
          return (
            <>
              {currOrder}
              {hasMoved && (
                <Icon
                  name="arrow_forward"
                  style={{
                    color: "var(--wlcm-color-blue)",
                    display: "inline-block",
                    transform: `rotate(${arrowRotateDeg}deg)`,
                    verticalAlign: "text-top",
                    marginLeft: "var(--wlcm-spacing-sm)",
                    opacity: 0.75,
                  }}
                />
              )}
            </>
          );
        },
        header: t("SNACK_ADMIN.MENU.CATEGORY.SORT_ORDER"),
      }),
      columnHelper.accessor((row) => row.name[i18n.language as "en" | "no"], {
        id: "name",
        cell: (info) => info.getValue(),
        header: t("SNACK_ADMIN.MENU.CATEGORY.NAME"),
      }),
      columnHelper.accessor("createdAt", {
        cell: (info) => {
          const timestamp = info.getValue();
          if (!timestamp) return "-";
          return FULL_DATE_LOCALE(i18n.language).format(
            firebaseTimestampToDate(timestamp),
          );
        },
        header: t("USERS.TABLE_HEADERS.CREATED_AT"),
      }),
      columnHelper.accessor("updatedAt", {
        cell: (info) => {
          const timestamp = info.getValue();
          if (!timestamp) return "-";
          return FULL_DATE_LOCALE(i18n.language).format(
            firebaseTimestampToDate(timestamp),
          );
        },
        header: t("COMMUNICATION.UPDATED_AT"),
      }),
      columnHelper.accessor(
        ({ linkedItemsCount, hiddenLinkedItemsCount, isHidden }) => ({
          linkedItemsCount,
          hiddenLinkedItemsCount,
          isHidden,
        }),
        {
          id: "products",
          cell: (info) => {
            const { linkedItemsCount, hiddenLinkedItemsCount, isHidden } =
              info.getValue();
            return (
              <>
                {linkedItemsCount} {t("TOTAL")}
                {hiddenLinkedItemsCount > 0 && (
                  <>
                    ,{" "}
                    <span
                      style={{
                        opacity: isHidden ? 1 : 0.5,
                        color: isHidden
                          ? "var(--wlcm-color-danger-red)"
                          : "inherit",
                      }}
                    >
                      {isHidden
                        ? t("ALL").toLowerCase()
                        : hiddenLinkedItemsCount}{" "}
                      {t("HIDDEN").toLowerCase()}
                    </span>
                  </>
                )}
              </>
            );
          },
          header: t("SNACK_ADMIN.TABLE.PRODUCTS"),
        },
      ),
      ...(restaurantCutoffSettings
        ? [
            columnHelper.display({
              id: "cutoff",
              cell: (info) => {
                const categoryId = info.row.original.id;
                const cutoffs = cutoffsByCategory.get(categoryId) ?? [];
                if (!cutoffs.length) return "";
                const [first] = cutoffs;
                const isUniform = cutoffs.every(
                  (cutoff) =>
                    cutoff.isOverride === first.isOverride &&
                    isSameCutoffSettings(cutoff.settings, first.settings),
                );
                if (isUniform) {
                  return (
                    <CutoffCell
                      itemId={categoryId}
                      settings={first.settings}
                      isOverride={first.isOverride}
                      plural
                      closedOnWeekends={closedOnWeekends}
                    />
                  );
                }
                const countByKind: Partial<
                  Record<CutoffSettings["kind"], number>
                > = {};
                for (const { settings } of cutoffs) {
                  countByKind[settings.kind] =
                    (countByKind[settings.kind] ?? 0) + 1;
                }
                return (
                  <MixedCutoffCell
                    rowId={categoryId}
                    countByKind={countByKind}
                  />
                );
              },
              header: (info) => (
                <SortableTableHeader
                  info={info}
                  label={t("SNACK_ADMIN.MENU.TABLE.CUTOFF")}
                  disableSorting
                />
              ),
            }),
          ]
        : []),
      columnHelper.accessor("bumpVatToHighRate", {
        id: "bumpVat",
        cell: (info) => (info.getValue() ? t("YES") : ""),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("SNACK_ADMIN.MENU.CATEGORY.FORCED_VAT")}
            columnInfo={t("SNACK_ADMIN.MENU.CATEGORY.BUMP_VAT_LABEL")}
            disableSorting
          />
        ),
      }),
      columnHelper.display({
        id: "actions",
        cell: (info) => {
          const category = info.row.original;
          const categoryName = category.name[i18n.language as "en" | "no"];
          const { isHidden } = category;
          return (
            <CategoryTableActions
              category={category}
              hasUnsavedChanges={hasUnsavedChanges}
              isUpdatingVisibility={
                isUpdatingCategoryVisibility &&
                categoryVisibilityVariables?.categoryId === category.id
              }
              canEditCutoff={!!restaurantCutoffSettings}
              onToggleVisibility={() =>
                showConfirm({
                  title: isHidden
                    ? t("SNACK_ADMIN.MENU.CATEGORY.MAKE_VISIBLE.TOOLTIP")
                    : t("SNACK_ADMIN.MENU.CATEGORY.MAKE_HIDDEN.TOOLTIP"),
                  subtitle: isHidden
                    ? t("SNACK_ADMIN.MENU.CATEGORY.MAKE_VISIBLE.MODAL_CONTENT")
                    : t("SNACK_ADMIN.MENU.CATEGORY.MAKE_HIDDEN.MODAL_CONTENT"),
                  confirmButtonProps: { danger: false, success: true },
                  cancelButtonProps: { dark: true },
                  onConfirm: () =>
                    updateCategoryVisibilityMutation({
                      menuId: menu!.id,
                      categoryId: category.id,
                      categoryName,
                      isHidden: !isHidden,
                    }),
                })
              }
              onEdit={() => {
                setEditingCategory(category);
                setModalOpen(true);
              }}
              onEditCutoff={() => setCutoffCategory(category)}
              onDelete={() => {
                setLocalCategories(
                  localCategories.filter((c) => c.id !== category.id),
                );
                setHasUnsavedChanges(true);
              }}
            />
          );
        },
        header: () => (
          <div className="sr-only">{t("BUILDING_VISITORS.ACTIONS")}</div>
        ),
      }),
    ],
    [
      localCategories,
      hasUnsavedChanges,
      isUpdatingCategoryVisibility,
      categoryVisibilityVariables,
      restaurantCutoffSettings,
      closedOnWeekends,
      cutoffsByCategory,
    ],
  );

  const breadcrumbs = [
    { label: t("SIDEBAR.SNACK_MENU"), path: "/snack-admin/menu" },
    {
      label: t("SIDEBAR.SNACK_CATEGORIES"),
      path: `/snack-admin/menu/categories`,
    },
  ];

  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton fallbackPath={{ to: breadcrumbs[0].path }} />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "var(--wlcm-spacing-sm) var(--wlcm-spacing-md)",
          marginBottom: "var(--wlcm-spacing-sm)",
        }}
      >
        <h1>{t("SIDEBAR.SNACK_CATEGORIES")}</h1>
        <div style={{ display: "flex", justifyContent: "end" }}>
          <Button icon="add" onClick={() => setModalOpen(true)}>
            {t("SNACK_ADMIN.MENU.ADD_CATEGORY")}
          </Button>
        </div>
      </div>
      <Table
        name="menu-categories"
        data={localCategories}
        columns={columns}
        isLoading={isLoading}
        withPagination={false}
        draggableRows
        onRowOrderChange={handleRowOrderChange}
        onRowClick={
          hasUnsavedChanges
            ? undefined
            : (category) => {
                setEditingCategory(category);
                setModalOpen(true);
              }
        }
      />
      <SaveResetButtons
        showButtons={hasUnsavedChanges}
        loading={isUpdatingMenu}
        disabledSubmit={isUpdatingMenu}
        onSave={() => {
          updateMenuMutation(localCategories);
        }}
        onReset={() => {
          setLocalCategories(menu?.categories ?? []);
          setHasUnsavedChanges(false);
        }}
      />
      <CategoryModal
        open={modalOpen}
        category={editingCategory}
        onClose={() => {
          setModalOpen(false);
          setEditingCategory(null);
        }}
      />
      <ItemsCutoffModal
        target={cutoffTarget}
        onClose={() => setCutoffCategory(null)}
      />
    </>
  );
}
